import isEmpty from 'lodash/isEmpty';
import { formatTimestamp, DATE_FORMATS, TIME_FORMATS } from './formatTimestamp';

/**
 * Formats a person's name by concatenating non-empty first, middle, and last names.
 *
 * @param {string} [firstName=''] - The first name of the person.
 * @param {string} [middleName=''] - The middle name of the person.
 * @param {string} [lastName=''] - The last name of the person.
 * @returns {string} The formatted full name, with extra spaces removed.
 *
 * @example
 * formatPrintName('John', '', 'Doe'); // Returns: "John Doe"
 * formatPrintName('Alice', 'Marie', 'Smith'); // Returns: "Alice Marie Smith"
 * formatPrintName('', '', 'Brown'); // Returns: "Brown"
 */
export const formatPrintName = (
  firstName = '',
  middleName = '',
  lastName = ''
) => {
  const names = [firstName, middleName, lastName].filter(
    (name) => !isEmpty(name)
  );
  return names.join(' ').trim();
};

/**
 * Formats a phone number by combining the phone code and phone value.
 *
 * - If both `phoneCode` and `phoneValue` are missing, it returns an empty string.
 * - If only one is present, it returns that value (with phone code prefixed by `+` if needed).
 * - If both are present, it combines them with a space.
 * - Ensures that the phone code starts with a `+`. If not, it adds it.
 *
 * @param {string} [phoneCode=''] - The phone country code (e.g., "+1", "44").
 * @param {string} [phoneValue=''] - The actual phone number.
 * @returns {string} The formatted phone number, a partial value, or an empty string.
 *
 * @example
 * formatPrintPhone('+1', '1234567890'); // Returns: "+1 1234567890"
 * formatPrintPhone('44', '9876543210'); // Returns: "+44 9876543210"
 * formatPrintPhone('', '1234567890'); // Returns: "1234567890"
 * formatPrintPhone('+91', ''); // Returns: "+91"
 * formatPrintPhone('', ''); // Returns: ""
 */
export const formatPrintPhone = (phoneCode = '', phoneValue = '') => {
  if (isEmpty(phoneCode) && isEmpty(phoneValue)) {
    return '';
  }

  const formattedCode = phoneCode
    ? phoneCode.startsWith('+')
      ? phoneCode
      : `+${phoneCode}`
    : '';
  return `${formattedCode} ${phoneValue}`.trim();
};

/**
 * Formats a given timestamp into a readable date-time format.
 *
 * - If the input is missing or invalid, it returns an empty string.
 * - Uses the `formatTimestamp` utility to convert the timestamp into EU date format and 12-hour time format.
 *
 * @param {string|number|Date} addedOn - The timestamp to be formatted.
 * @returns {string} The formatted date-time string, or an empty string if invalid.
 *
 * @example
 * formatPrintDateTime("2024-03-02T14:30:00Z"); // Returns: "02/03/2024, 02:30 PM"
 * formatPrintDateTime(1709380200000); // Returns: "02/03/2024, 02:30 PM" (Epoch time)
 * formatPrintDateTime(""); // Returns: ""
 * formatPrintDateTime(null); // Returns: ""
 * formatPrintDateTime("invalid-date"); // Returns: ""
 */
export const formatPrintDateTime = (addedOn) => {
  if (isEmpty(addedOn) || isNaN(new Date(addedOn).getTime())) {
    return '';
  }

  const { date, time } = formatTimestamp(addedOn, {
    dateFormat: DATE_FORMATS.EU,
    timeFormat: TIME_FORMATS.TWELVE_HOUR,
  });

  return `${date}, ${time}`.trim();
};
